import apiClient from './api';

export interface UserListItem {
  id: string;
  username: string;
  name: string;
  role: string;
  organization?: string;
  phone?: string;
  email?: string;
  isProvider: boolean;
  businessLicense?: string;
  idCardFront?: string;
  idCardBack?: string;
  serviceCategoryIds?: string[];
  orderSourceWechat?: boolean;
  orderSourceMeituan?: boolean;
  wechatServiceRegions?: UserWechatServiceRegion[];
  meituanShopIds?: string[];
  withdrawableAmount?: number;
  settlementType?: 'whole_order' | 'sub_order';
  settlementRatio?: number;
  settlementAmountType?: 'remaining_amount_ratio' | 'paid_amount_ratio';
  settlementCycleDays?: number;
  status: string;
  lastLogin?: string;
  createdAt: string;
}

export interface UserWechatServiceRegion {
  regionId: string;
  provinceName: string;
  cityName: string;
  districtName: string;
}

export interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  perPage: number;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  name: string;
  role: string;
  organizationId?: string;
  organization?: string;
  phone?: string;
  email?: string;
  isProvider?: boolean;
  businessLicense?: string;
  idCardFront?: string;
  idCardBack?: string;
  serviceCategoryIds?: string[];
  orderSourceWechat?: boolean;
  orderSourceMeituan?: boolean;
  wechatServiceRegions?: UserWechatServiceRegion[];
  meituanShopIds?: string[];
  settlementType?: 'whole_order' | 'sub_order';
  settlementRatio?: number;
  settlementAmountType?: 'remaining_amount_ratio' | 'paid_amount_ratio';
  settlementCycleDays?: number;
}

export interface UserProviderSettlementFlow {
  id: string;
  providerId: string;
  providerUsername: string;
  providerName: string;
  providerPhone: string;
  amount: number;
  beforeAmount: number;
  afterAmount: number;
  operatorId: string;
  operatorName: string;
  remark: string;
  invoiceUrl: string;
  settledAt?: string;
  createdAt: string;
}

export interface UserProviderSettlementSummary {
  provider: UserListItem;
  flows: UserProviderSettlementFlow[];
}

export interface UserProviderOrderSettlementFlow {
  id: string;
  providerId: string;
  providerUsername: string;
  providerName: string;
  providerPhone: string;
  orderId: string;
  orderNo: string;
  orderItemId: string;
  orderItemNo: string;
  orderSource: string;
  meituanReservationNo: string;
  settlementType: 'whole_order' | 'sub_order';
  settlementRatio: number;
  settlementAmountType: 'remaining_amount_ratio' | 'paid_amount_ratio';
  orderAmount: number;
  estimatedAmount: number;
  settlementAmount: number;
  settlementCycleDays: number;
  status: 'pending' | 'withdrawable';
  availableAt?: string;
  settledAt?: string;
  createdAt: string;
}

export interface UserProviderOrderSettlementSummary {
  provider: UserListItem;
  flows: UserProviderOrderSettlementFlow[];
}

export interface ProviderFlowListResponse<T> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProviderFlowListParams {
  page?: number;
  pageSize?: number;
  providerName?: string;
  providerPhone?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface ProviderIncomeFlowTrendItem {
  date: string;
  amount: number;
  count: number;
}

export interface CreateUserProviderSettlementRequest {
  amount: number;
  operatorName?: string;
  remark?: string;
  invoiceUrl?: string;
}

export interface UpdateUserRequest {
  name?: string;
  role?: string;
  organizationId?: string;
  organization?: string;
  phone?: string;
  email?: string;
  isProvider?: boolean;
  businessLicense?: string;
  idCardFront?: string;
  idCardBack?: string;
  serviceCategoryIds?: string[];
  orderSourceWechat?: boolean;
  orderSourceMeituan?: boolean;
  wechatServiceRegions?: UserWechatServiceRegion[];
  meituanShopIds?: string[];
  settlementType?: 'whole_order' | 'sub_order';
  settlementRatio?: number;
  settlementAmountType?: 'remaining_amount_ratio' | 'paid_amount_ratio';
  settlementCycleDays?: number;
  status?: string;
}

export const userService = {
  list: async (params?: any): Promise<UserListResponse> => {
    const response = await apiClient.get<UserListResponse>('/users', { params });
    return response.data;
  },

  get: async (id: string): Promise<UserListItem> => {
    const response = await apiClient.get<UserListItem>(`/users/${id}`);
    return response.data;
  },

  create: async (data: CreateUserRequest): Promise<UserListItem> => {
    const response = await apiClient.post<UserListItem>('/users', data);
    return response.data;
  },

  update: async (id: string, data: UpdateUserRequest): Promise<void> => {
    await apiClient.put(`/users/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  resetPassword: async (id: string, newPassword: string): Promise<void> => {
    await apiClient.put(`/users/${id}/reset-password`, { newPassword });
  },

  getProviderSettlementFlows: async (id: string): Promise<UserProviderSettlementSummary> => {
    const response = await apiClient.get<UserProviderSettlementSummary>(`/users/${id}/provider-settlement-flows`);
    return response.data;
  },

  createProviderSettlementFlow: async (id: string, data: CreateUserProviderSettlementRequest): Promise<UserProviderSettlementSummary> => {
    const response = await apiClient.post<UserProviderSettlementSummary>(`/users/${id}/provider-settlement-flows`, data);
    return response.data;
  },

  listAllProviderSettlementFlows: async (params?: ProviderFlowListParams): Promise<ProviderFlowListResponse<UserProviderSettlementFlow>> => {
    const response = await apiClient.get<ProviderFlowListResponse<UserProviderSettlementFlow>>('/provider-flows/settlements', { params });
    return response.data;
  },

  listProviderSettlementFlowTrend: async (params?: ProviderFlowListParams): Promise<ProviderIncomeFlowTrendItem[]> => {
    const response = await apiClient.get<ProviderIncomeFlowTrendItem[]>('/provider-flows/settlements/trend', { params });
    return response.data;
  },

  listAllProviderIncomeFlows: async (params?: ProviderFlowListParams): Promise<ProviderFlowListResponse<UserProviderOrderSettlementFlow>> => {
    const response = await apiClient.get<ProviderFlowListResponse<UserProviderOrderSettlementFlow>>('/provider-flows/incomes', { params });
    return response.data;
  },

  listProviderIncomeFlowTrend: async (params?: ProviderFlowListParams): Promise<ProviderIncomeFlowTrendItem[]> => {
    const response = await apiClient.get<ProviderIncomeFlowTrendItem[]>('/provider-flows/incomes/trend', { params });
    return response.data;
  },
};
